import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { useTranslation } from "react-i18next";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

const LANG_BASE = "vessel-call.port-services";
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const MAX_DIGITS = 9;
const NUMBER_OF_PACKAGES_LABEL_KEY = `${LANG_BASE}.fields.number-of-packages-handled`;

interface NumberOfPackagesHandledFieldProps {
  serviceDetails: {
    numberOfPackagesHandled?: string | number | undefined;
    serviceCategory?: string;
    serviceType?: string;
    unitOfMeasurement?: string;
  };
}

const NumberOfPackagesHandledField = ({
  serviceDetails: { numberOfPackagesHandled: currentNumberOfPackagesHandled },
}: NumberOfPackagesHandledFieldProps) => {
  const { setNumberOfPackagesHandled, errors, setErrors } = usePortServicesFlow();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [hasMaxDigitsError, setHasMaxDigitsError] = useState(false);
  const numberOfPackagesLabel = t(NUMBER_OF_PACKAGES_LABEL_KEY);

  useEffect(() => {
    const parsedNumberOfPackagesHandled =
      typeof currentNumberOfPackagesHandled === "string"
        ? currentNumberOfPackagesHandled.trim() === ""
          ? undefined
          : Number(currentNumberOfPackagesHandled)
        : currentNumberOfPackagesHandled;

    const validNumberOfPackagesHandled =
      parsedNumberOfPackagesHandled == null ||
      Number.isNaN(parsedNumberOfPackagesHandled as number) ||
      !Number.isInteger(parsedNumberOfPackagesHandled) ||
      parsedNumberOfPackagesHandled <= 0
        ? undefined
        : parsedNumberOfPackagesHandled;

    setNumberOfPackagesHandled(validNumberOfPackagesHandled);
    setInputValue(validNumberOfPackagesHandled != null ? String(validNumberOfPackagesHandled) : "");
  }, [currentNumberOfPackagesHandled, setNumberOfPackagesHandled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setInputValue("");
      setNumberOfPackagesHandled(undefined);
      setHasMaxDigitsError(false);
      if (errors.numberOfPackagesHandled) {
        setErrors({
          ...errors,
          numberOfPackagesHandled: undefined,
        });
      }
      return;
    }

    if (!POSITIVE_INTEGER_PATTERN.test(value)) return;

    if (value.length > MAX_DIGITS) {
      setHasMaxDigitsError(true);
      setErrors({
        ...errors,
        numberOfPackagesHandled: t("form-validation.must-have-maximum-digits", {
          label: numberOfPackagesLabel,
          max: MAX_DIGITS,
        }),
      });
      return;
    }

    const numberValue = Number(value);

    setInputValue(value);
    setNumberOfPackagesHandled(Number.isNaN(numberValue) ? undefined : numberValue);
    setHasMaxDigitsError(false);

    if (errors.numberOfPackagesHandled) {
      setErrors({
        ...errors,
        numberOfPackagesHandled: undefined,
      });
    }
  };

  const handleBlur = () => {
    if (hasMaxDigitsError && errors.numberOfPackagesHandled) {
      setHasMaxDigitsError(false);
      setErrors({
        ...errors,
        numberOfPackagesHandled: undefined,
      });
      return;
    }

    if (inputValue === "") {
      setErrors({
        ...errors,
        numberOfPackagesHandled: t("form-validation.required", {
          label: numberOfPackagesLabel,
        }),
      });
      return;
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <CustomFormLabel data-testid="numberOfPackagesHandled-label">
        {numberOfPackagesLabel}
      </CustomFormLabel>
      <Input
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.numberOfPackagesHandled !== undefined}
        testId={"number-of-packages-handled-input"}
        sx={{ width: "100%" }}
        placeholder={numberOfPackagesLabel}
        fullWidth
      />
      {errors.numberOfPackagesHandled && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {errors.numberOfPackagesHandled}
        </Typography>
      )}
    </div>
  );
};

export default NumberOfPackagesHandledField;
